import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UserProvider } from '@siat-fe/auth';
import { WidgetDataStore } from '../../../stores/widget-data.store';
import { Widget, WidgetType } from '../models';
import { getWidgetTitleId } from '../models/widget.utils';

@Component({
  selector: 'siat-widget-base',
  imports: [
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    CdkDragHandle,
    TranslatePipe,
    MatTooltip,
    NgClass,
  ],
  templateUrl: './widget-base.component.html',
  styleUrl: './widget-base.component.scss',
})
export class WidgetBaseComponent implements OnInit, OnDestroy {
  @Input() widget!: Widget;
  @Input() isEditMode = false;

  @Output() remove = new EventEmitter<Widget>();
  @Output() refresh = new EventEmitter<Widget>();

  private widgetDataStore = inject(WidgetDataStore);
  private router = inject(Router);
  private userProvider = inject(UserProvider);
  translate = inject(TranslateService);

  // Computed signals for reactive state management
  isLoading = computed(() =>
    this.widgetDataStore.isWidgetLoading(this.widget?.id),
  );
  hasError = computed(
    () => !!this.widgetDataStore.getWidgetError(this.widget?.id),
  );
  lastUpdated = computed(() =>
    this.widgetDataStore.getWidgetLastUpdated(this.widget?.id),
  );
  widgetData = computed(() =>
    this.widgetDataStore.getWidgetData(this.widget?.id),
  );

  ngOnInit() {
    // Initialize widget data loading when component starts
    if (this.widget?.config?.api?.endpoint) {
      this.widgetDataStore.initializeWidget(this.widget);
    }
  }

  ngOnDestroy() {
    // Clean up auto-refresh intervals when component is destroyed
    if (this.widget?.id) {
      this.widgetDataStore.clearAutoRefresh(this.widget.id);
    }
  }

  onRemove() {
    this.remove.emit(this.widget);
    // Clean up widget data from store
    if (this.widget?.id) {
      this.widgetDataStore.removeWidget(this.widget.id);
    }
  }

  onRefresh() {
    // Refresh widget data through the store
    if (this.widget) {
      this.widgetDataStore.initializeWidget(this.widget);
    }
    this.refresh.emit(this.widget);
  }

  getLastUpdatedTime(): string {
    const updated = this.lastUpdated();
    if (!updated) return 'Mai';

    const now = new Date();
    const diffMs = now.getTime() - updated.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'Ora';
    if (diffMins < 60) return `${diffMins}m fa`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h fa`;

    return updated.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  protected readonly WidgetType = WidgetType;

  /**
   * Id of the heading that visibly labels this widget, for aria-labelledby.
   * @returns {string} The heading element id.
   */
  get headerLabelId(): string {
    return getWidgetTitleId(this.widget.id);
  }

  openFullList() {
    if (this.widget.id === 'allergieIntolleranze') {
      this.router.navigateByUrl(
        `/home/scheda-paziente/${this.widget.config.api.body?.['idPaziente']}/${this.widget.config.path.viewAll}`,
      );
    } else if (this.widget.id === 'documenti') {
      const idsPazienti = this.widget.config.api.body?.['idsPazienti'] as
        | number[]
        | undefined;
      const patientId = idsPazienti?.[0];
      const path = this.widget.config.path.viewAll;
      this.router.navigateByUrl(`/home/scheda-paziente/${patientId}/${path}`);
    } else {
      this.router.navigateByUrl(`/home/${this.widget.config.path.viewAll}`);
    }
  }
}
